'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import { Phone, MessageCircle, Calendar, CheckCircle, Star, Clock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';


export default function AgentCard() {
  const [showPhone, setShowPhone] = useState(false);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const router = useRouter();

  const AGENT = {
    id: 'agent-selin-demirci',
    name: 'Selin Demirci',
    title: 'Kıdemli Emlak Danışmanı',
    agency: 'Kadıköy Premium Emlak',
    phone: '0532 123 45 67',
    responseTime: '< 2 saat',
    rating: 4.9,
    reviews: 127,
    listings: 34,
    verified: true,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1152b30b9-1763301565633.png",
    imageAlt: 'Profesyonel emlak danışmanı, kadın, takım elbise'
  };

  const handleSendMessage = async () => {
    if (!message?.trim()) return;
    if (!user) {
      router?.push('/giris');
      return;
    }
    setSending(true);
    try {
      // Get agent's user profile id — in production this would come from the property data
      // For now we create/find a conversation and redirect
      router?.push('/mesajlar');
    } catch {
      // silently fail
    } finally {
      setSending(false);
      setShowMessageForm(false);
      setMessage('');
    }
  };

  const handleTourRequest = () => {
    if (!user) {
      router?.push('/giris');
      return;
    }
    router?.push('/mesajlar');
  };

  return (
    <div className="space-y-4">
      {/* Agent Info */}
      <div className="flex items-center gap-3">
        <div className="relative">
          <AppImage
            src={AGENT?.image}
            alt={AGENT?.imageAlt}
            width={56}
            height={56}
            className="w-14 h-14 rounded-2xl object-cover" />
          
          {AGENT?.verified &&
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-success flex items-center justify-center border-2 border-white">
              <CheckCircle size={11} className="text-white" />
            </div>
          }
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-foreground">{AGENT?.name}</p>
          <p className="text-xs text-muted-foreground">{AGENT?.title}</p>
          <p className="text-xs text-primary font-semibold">{AGENT?.agency}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        {[
        { label: 'Puan', value: AGENT?.rating?.toString(), icon: <Star size={12} className="text-warning" fill="currentColor" /> },
        { label: 'Yorum', value: AGENT?.reviews?.toString(), icon: <MessageCircle size={12} className="text-primary" /> },
        { label: 'İlan', value: AGENT?.listings?.toString(), icon: <CheckCircle size={12} className="text-success" /> }]?.
        map((stat, i) =>
        <div key={`agent-stat-${i}`} className="bg-muted rounded-xl p-2.5 text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">{stat?.icon}</div>
            <p className="text-base font-extrabold text-foreground tabular-nums">{stat?.value}</p>
            <p className="text-[10px] text-muted-foreground font-medium">{stat?.label}</p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Clock size={12} className="text-success" />
        <span>Yanıt süresi: <span className="font-semibold text-success">{AGENT?.responseTime}</span></span>
      </div>

      {/* Actions */}
      <div className="space-y-2">
        <button
          onClick={() => setShowPhone(!showPhone)}
          className="w-full btn-primary justify-center gap-2 py-3">
          <Phone size={16} />
          {showPhone ? AGENT?.phone : 'Telefonu Göster'}
        </button>

        <button
          onClick={() => setShowMessageForm(!showMessageForm)}
          className="w-full btn-secondary justify-center gap-2 py-3">
          <MessageCircle size={16} />
          Mesaj Gönder
        </button>

        <button
          onClick={handleTourRequest}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-all">
          <Calendar size={16} className="text-primary" />
          Tur Talep Et
        </button>
      </div>

      {/* Message Form */}
      {showMessageForm &&
      <div className="border-t border-border pt-4 animate-fade-in">
          <p className="text-xs font-bold text-foreground mb-2">Mesajınız</p>
          <textarea
          value={message}
          onChange={(e) => setMessage(e?.target?.value)}
          rows={3}
          placeholder="Bu ilan hakkında bilgi almak istiyorum..."
          className="w-full px-3 py-2.5 rounded-xl border border-border text-sm resize-none focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10" />
        
          <div className="flex gap-2 mt-2">
            <button
            onClick={() => setShowMessageForm(false)}
            className="flex-1 btn-secondary text-sm py-2">
              İptal
            </button>
            <button
              onClick={handleSendMessage}
              disabled={sending || !message?.trim()}
              className="flex-1 btn-primary text-sm py-2 disabled:opacity-50">
              {sending ? 'Gönderiliyor...' : 'Gönder'}
            </button>
          </div>
        </div>
      }

      {/* Safety Note */}
      <div className="flex items-start gap-2 p-3 rounded-xl bg-muted">
        <CheckCircle size={13} className="text-success flex-shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Evora güvencesi: Ödeme yapmadan önce mülkü bizzat görün. Şüpheli durumları bildirin.
        </p>
      </div>
    </div>
  );

}